import { useState, useMemo, useEffect } from 'react';
import { useNetworkMonitor } from '../../contexts/NetworkMonitorContext';
import './NetworkConsole.css';

const NetworkConsole = () => {
  const { requests, isEnabled, isConsoleVisible, clearRequests, toggleMonitoring, toggleConsole } = useNetworkMonitor();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [height, setHeight] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  // 필터링된 요청 목록
  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      const matchesSearch = !filter || 
        req.url.toLowerCase().includes(filter.toLowerCase()) ||
        req.method.toLowerCase().includes(filter.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'success' && req.statusCode >= 200 && req.statusCode < 300) ||
        (statusFilter === 'error' && (req.statusCode >= 400 || req.error)) ||
        (statusFilter === 'pending' && req.status === 'pending');

      return matchesSearch && matchesStatus;
    });
  }, [requests, filter, statusFilter]);

  // 상태 코드에 따른 색상
  const getStatusColor = (statusCode) => {
    if (!statusCode) return 'gray';
    if (statusCode >= 200 && statusCode < 300) return 'green';
    if (statusCode >= 300 && statusCode < 400) return 'blue';
    if (statusCode >= 400) return 'red';
    return 'gray';
  };

  // 메서드에 따른 색상
  const getMethodColor = (method) => {
    const colors = {
      GET: 'blue',
      POST: 'green',
      PUT: 'orange',
      PATCH: 'yellow',
      DELETE: 'red',
    };
    return colors[method] || 'gray';
  };

  // 시간 포맷
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('ko-KR', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  // 크기 포맷
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  // 지속 시간 포맷
  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  };

  // JSON 포맷팅
  const formatJSON = (data) => {
    if (!data) return '';
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  // 높이 조절 핸들러
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // 전역 마우스 이벤트 리스너
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newHeight = window.innerHeight - e.clientY;
      const minHeight = 200;
      const maxHeight = window.innerHeight - 100;
      setHeight(Math.max(minHeight, Math.min(maxHeight, newHeight)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isConsoleVisible) {
    return null;
  }

  return (
    <div className="network-console" style={{ height: `${height}px` }}>
      <div 
        className="network-console-resize-handle"
        onMouseDown={handleMouseDown}
      />
      <div className="network-console-header flex align-center justify-between px-16 py-10">
        <div className="flex align-center gap-10">
          <h3 className="text-b2-700 text-gray-800">Network Console</h3>
          <button
            className="btn icon-btn text-gray-600"
            onClick={toggleConsole}
            title="Hide Network Console"
          >
            ✕
          </button>
          <button 
            className={`btn small-btn ${isEnabled ? 'btn-primary' : 'btn-style-4'}`}
            onClick={toggleMonitoring}
          >
            {isEnabled ? 'ON' : 'OFF'}
          </button>
          <button 
            className="btn small-btn btn-style-4"
            onClick={clearRequests}
            disabled={requests.length === 0}
          >
            Clear
          </button>
          <span className="text-c1-400 text-gray-600">
            {requests.length} requests
          </span>
        </div>
        <div className="flex align-center gap-10">
          <input
            type="text"
            placeholder="Filter by URL or method..."
            className="input-primary"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ width: '200px' }}
          />
          <select
            className="input-primary"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      <div className="network-console-content flex">
        <div className="network-console-list flex-1">
          <table className="network-table">
            <thead>
              <tr className="bg-body">
                <th>Status</th>
                <th>Method</th>
                <th>URL</th>
                <th>Time</th>
                <th>Duration</th>
                <th>Size</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center text-c1-400 text-gray-600 py-20">
                    요청이 없습니다.
                  </td>
                </tr>
              ) : (
                filteredRequests.map((req) => (
                  <tr
                    key={req.id}
                    className={selectedRequest?.id === req.id ? 'selected' : ''}
                    onClick={() => setSelectedRequest(req)}
                  >
                    <td>
                      <span 
                        className={`network-status-badge ${getStatusColor(req.statusCode)}`}
                      >
                        {req.statusCode || (req.status === 'pending' ? 'Pending' : 'Error')}
                      </span>
                    </td>
                    <td>
                      <span 
                        className={`network-method-badge ${getMethodColor(req.method)}`}
                      >
                        {req.method}
                      </span>
                    </td>
                    <td className="url-cell">
                      <span className="text-c1-400 text-gray-700" title={req.url}>
                        {req.url}
                      </span>
                    </td>
                    <td className="text-c2-400 text-gray-600">
                      {formatTime(req.timestamp)}
                    </td>
                    <td className="text-c2-400 text-gray-600">
                      {formatDuration(req.duration)}
                    </td>
                    <td className="text-c2-400 text-gray-600">
                      {formatSize(req.responseSize)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {selectedRequest && (
          <div className="network-console-detail flex flex-col">
            <div className="network-detail-header flex align-center justify-between px-16 py-10 border-b">
              <h4 className="text-b2-700 text-gray-800">Request Details</h4>
              <button 
                className="btn icon-btn text-gray-600"
                onClick={() => setSelectedRequest(null)}
              >
                ✕
              </button>
            </div>
            <div className="network-detail-content flex-1 overflow-y-scroll">
              <div className="network-detail-section">
                <h5 className="text-b2-700 text-gray-700 mb-10">General</h5>
                <div className="network-detail-item">
                  <span className="label">Request URL:</span>
                  <span className="value">{selectedRequest.url}</span>
                </div>
                <div className="network-detail-item">
                  <span className="label">Request Method:</span>
                  <span className="value">{selectedRequest.method}</span>
                </div>
                <div className="network-detail-item">
                  <span className="label">Status Code:</span>
                  <span className="value">{selectedRequest.statusCode || 'N/A'}</span>
                </div>
                <div className="network-detail-item">
                  <span className="label">Duration:</span>
                  <span className="value">{formatDuration(selectedRequest.duration)}</span>
                </div>
                <div className="network-detail-item">
                  <span className="label">Size:</span>
                  <span className="value">{formatSize(selectedRequest.responseSize)}</span>
                </div>
              </div>
{/* 
              <div className="network-detail-section">
                <h5 className="text-b2-700 text-gray-700 mb-10">Request Headers</h5>
                <pre className="network-code-block">
                  {formatJSON(selectedRequest.requestHeaders)}
                </pre>
              </div> */}

              {selectedRequest.requestBody && (
                <div className="network-detail-section">
                  <h5 className="text-b2-700 text-gray-700 mb-10">Request Payload</h5>
                  <pre className="network-code-block">
                    {formatJSON(selectedRequest.requestBody)}
                  </pre>
                </div>
              )}

              {/* {selectedRequest.responseHeaders && (
                <div className="network-detail-section">
                  <h5 className="text-b2-700 text-gray-700 mb-10">Response Headers</h5>
                  <pre className="network-code-block">
                    {formatJSON(selectedRequest.responseHeaders)}
                  </pre>
                </div>
              )} */}

              {selectedRequest.responseData && (
                <div className="network-detail-section">
                  <h5 className="text-b2-700 text-gray-700 mb-10">Response</h5>
                  <pre className="network-code-block">
                    {formatJSON(selectedRequest.responseData)}
                  </pre>
                </div>
              )}

              {selectedRequest.error && (
                <div className="network-detail-section">
                  <h5 className="text-b2-700 text-red-500 mb-10">Error</h5>
                  <pre className="network-code-block error">
                    {formatJSON(selectedRequest.error)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkConsole;

